import type { UnpublishModalProps } from "@v2/types/modalTypes";

import React from "react";

import { Alert } from "@replo/design-system/blocks/alert/Alert";
import { Button } from "@replo/design-system/components/button/Button";
import { Drawer } from "@replo/design-system/components/drawer/Drawer";
import { Modal } from "@replo/design-system/components/modal/Modal";
import { toast } from "@replo/design-system/components/toast/Toast";
import { useIsMobile } from "@replo/design-system/hooks/use-mobile";
import usePublishedSiteUrls from "@v2/features/home/hooks/usePublishedSiteUrls";
import { invalidateResourcesRelatedToDeployment } from "@v2/features/page-renderer/components/navbar/invalidateResourcesRelatedToDeployment";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import { funnelUtils } from "@v2/trpc/routes/funnel";
import { nextJSProjectUtils } from "@v2/trpc/routes/nextJSProject";
import { v2ShopDeploymentRouter } from "@v2/trpc/routes/v2ShopDeployment";
import { ExternalLink } from "lucide-react";

export const UnpublishModal: React.FC<
  UnpublishModalProps & {
    open: boolean;
    onOpenChange: (open: boolean) => void;
  }
> = ({ open, onOpenChange, ...props }) => {
  const isMobile = useIsMobile();
  const logEvent = useLogAnalytics();
  const projectId = useCurrentProjectId();

  const page = props.type === "page" ? props.page : undefined;
  const funnel = props.type === "funnel" ? props.funnel : undefined;

  const filesToUnpublish = React.useMemo(() => {
    if (page) {
      return page.lastPublishedFileVersionId ? [page] : [];
    } else if (funnel) {
      return funnel.funnelSteps
        .map((step) => step.file)
        .filter((file): file is NonNullable<typeof file> => {
          return Boolean(file?.id && file?.lastPublishedFileVersionId);
        });
    }
    return [];
  }, [page, funnel]);

  const publishedPageCount = filesToUnpublish.length;

  const { mutate: unpublish } = v2ShopDeploymentRouter.deploy.useMutation({
    onMutate: async ({ fileIdsToUnpublish }) => {
      if (!projectId) {
        return;
      }

      let previousFunnel;
      let previousFunnelList;

      // Only update funnel caches if we're unpublishing a funnel
      if (props.type === "funnel") {
        await funnelUtils.find.cancel({
          projectId,
          funnelId: props.funnel.id,
        });

        previousFunnel = funnelUtils.find.getData({
          projectId,
          funnelId: props.funnel.id,
        });

        // NOTE (Cole, 2025-07-21): Optimistically update the funnel.find cache - set lastPublishedFileVersionId to null for unpublished files
        funnelUtils.find.setData(
          { projectId, funnelId: props.funnel.id },
          (oldFunnel) => {
            if (!oldFunnel) {
              return oldFunnel;
            }

            return {
              ...oldFunnel,
              funnelSteps: oldFunnel.funnelSteps.map((step) => {
                if (
                  step.file?.id &&
                  fileIdsToUnpublish.includes(step.file.id)
                ) {
                  return {
                    ...step,
                    file: {
                      ...step.file,
                      lastPublishedFileVersionId: null,
                    },
                  };
                }
                return step;
              }),
            };
          },
        );

        await funnelUtils.list.cancel({
          projectId,
          includeFiles: true,
        });

        previousFunnelList = funnelUtils.list.getData({
          projectId,
          includeFiles: true,
        });

        // NOTE (Cole, 2025-07-21): Optimistically update the funnel.list cache - set lastPublishedFileVersionId to null for unpublished files
        funnelUtils.list.setData(
          { projectId, includeFiles: true },
          (oldFunnelList) => {
            if (!oldFunnelList) {
              return oldFunnelList;
            }

            return oldFunnelList.map((funnel) => {
              if (funnel.id === props.funnel.id) {
                return {
                  ...funnel,
                  funnelSteps: funnel.funnelSteps.map((step) => {
                    if (
                      step.file?.id &&
                      fileIdsToUnpublish.includes(step.file.id)
                    ) {
                      return {
                        ...step,
                        file: {
                          ...step.file,
                          lastPublishedFileVersionId: null,
                        },
                      };
                    }
                    return step;
                  }),
                };
              }
              return funnel;
            });
          },
        );
      }

      // NOTE (Cole, 2025-07-21): Also optimistically update individual page component caches
      const previousPageCaches: Record<string, any> = {};

      fileIdsToUnpublish.forEach((fileId) => {
        void nextJSProjectUtils.getPage.cancel({
          projectId,
          id: fileId,
        });

        const previousPageData = nextJSProjectUtils.getPage.getData({
          projectId,
          id: fileId,
        });

        if (previousPageData) {
          previousPageCaches[fileId] = previousPageData;

          nextJSProjectUtils.getPage.setData(
            { projectId, id: fileId },
            {
              ...previousPageData,
              lastPublishedFileVersionId: null,
            },
          );
        }
      });

      return { previousFunnel, previousFunnelList, previousPageCaches };
    },
    onError: (_err, { fileIdsToUnpublish }, context) => {
      if (context?.previousFunnel && projectId && props.type === "funnel") {
        funnelUtils.find.setData(
          { projectId, funnelId: props.funnel.id },
          context.previousFunnel,
        );
      }

      if (context?.previousFunnelList && projectId && props.type === "funnel") {
        funnelUtils.list.setData(
          { projectId, includeFiles: true },
          context.previousFunnelList,
        );
      }

      if (context?.previousPageCaches && projectId) {
        fileIdsToUnpublish.forEach((fileId) => {
          const previousPageData = context.previousPageCaches[fileId];
          if (previousPageData) {
            nextJSProjectUtils.getPage.setData(
              { projectId, id: fileId },
              previousPageData,
            );
          }
        });
      }
    },
    onSettled: (_data, _error, { fileIdsToUnpublish }) => {
      if (props.type === "funnel") {
        void funnelUtils.find.invalidate({
          projectId,
          funnelId: props.funnel.id,
        });
        void funnelUtils.list.invalidate({
          projectId,
          includeFiles: true,
        });
      }

      if (projectId) {
        fileIdsToUnpublish.forEach((fileId) => {
          void nextJSProjectUtils.getPage.invalidate({
            projectId,
            id: fileId,
          });
        });
      }

      invalidateResourcesRelatedToDeployment();
    },
  });

  const publishedSiteUrls = usePublishedSiteUrls({
    pageRoutesToUseForPreview: (() => {
      if (props.type === "funnel") {
        return props.funnel.funnelSteps
          .map((step) => {
            return step.file?.pageRoute;
          })
          .filter((route): route is string => Boolean(route));
      } else if (props.page.pageRoute) {
        return [props.page.pageRoute];
      } else {
        return [];
      }
    })(),
  });

  if (!projectId) {
    return null;
  }

  const handleUnpublish = () => {
    logEvent("page.unpublish");
    void unpublish({
      projectId,
      fileIdsToPublish: [],
      fileIdsToUnpublish: filesToUnpublish.map((file) => file.id),
    });

    const itemType = props.type === "page" ? "Page" : "Pages";
    toast.success({
      title: `${itemType} Unpublished`,
      description: `${publishedPageCount} page${publishedPageCount !== 1 ? "s have" : " has"} been unpublished - wait a few minutes to see changes to your live site.`,
    });

    onOpenChange(false);
  };

  const modalTitle =
    props.type === "page"
      ? "Unpublish Page"
      : `Unpublish ${publishedPageCount} Page${publishedPageCount !== 1 ? "s" : ""}`;

  const alertText =
    props.type === "page"
      ? "This page will no longer be live. Visitors will see a 404 page if they access its URL."
      : "These pages will no longer be live. Visitors will see a 404 page if they access their URLs.";

  const footer = (
    <div className="flex flex-row gap-2 justify-end w-full">
      <Button
        variant="secondary"
        size="base"
        onClick={() => onOpenChange(false)}
      >
        Cancel
      </Button>
      <Button
        variant="destructive"
        size="base"
        onClick={handleUnpublish}
        data-testid="unpublish-funnel-button"
      >
        Unpublish
      </Button>
    </div>
  );

  const content = (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        {filesToUnpublish.map((file, index) => {
          return (
            <div
              key={file.id}
              className="flex items-center justify-between p-3 border border-border rounded-md bg-background"
            >
              <div className="flex flex-col gap-1">
                <div className="header-h6">{file.name || "Untitled Page"}</div>
                <div className="text-xs text-placeholder">
                  {file.pageRoute ?? "/"}
                </div>
              </div>
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => {
                  if (publishedSiteUrls[index]) {
                    window.open(publishedSiteUrls[index], "_blank");
                  }
                }}
              >
                <ExternalLink />
              </Button>
            </div>
          );
        })}
      </div>

      <Alert variant="warning" description={alertText} />
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={onOpenChange}
        title={modalTitle}
        footer={footer}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Modal
      title={modalTitle}
      open={open}
      onOpenChange={onOpenChange}
      size="sm"
      footer={footer}
    >
      {content}
    </Modal>
  );
};
